import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import QRCode from 'react-qr-code';
import { useGoogle2FAQrCode } from '@services/useGoogle2FAQrCode';
import { useGoogle2FAVerify } from '@services/useGoogle2FAVerify';
import { useData } from '@hooks/useData';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Space } from '@components/atoms/Space';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useGetCart } from '@services/useGetCart';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useNotifications } from '@hooks/useNotifications';
import { SITE_URLS } from '@constants';

interface TwoFactorAuthFormErrors {
    secret: string[];
}

export const TwoFactorAuthActivateCard: FC = () => {
    const { t, i18n } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const { qrCode: qrCodeFromData, login } = useData();
    const { request } = useGoogle2FAQrCode();
    const { request: verifyRequest, loading, error, lastResponseTime } = useGoogle2FAVerify();
    const { request: requestCart } = useGetCart();
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();

    const [qrCode, setQrCode] = useState<string | null>(null);
    const [secret, setSecret] = useState<string>('');
    const [errors, setErrors] = useState<TwoFactorAuthFormErrors>({ secret: [] });
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const translations = {
        title: t('two-factory-auth-activate-card-component.title'),
        secretInvalidError: t('two-factory-auth-activate-card-component.secret-invalid-error'),
        secretSuccess: t('two-factory-auth-activate-card-component.secret-success'),
        checkQR: t('two-factory-auth-activate-card-component.submit'),
    };

    const validateForm: () => TwoFactorAuthFormErrors = () => {
        const newErrors: TwoFactorAuthFormErrors = { secret: [] };
        if (!secret.trim()) {
            newErrors.secret.push(translations.secretInvalidError);
        }
        setErrors(newErrors);
        return newErrors;
    };

    const submitForm = async () => {
        const validationErrors = validateForm();
        if (validationErrors.secret.length > 0) return;
        setHasSubmitted(true);
        await verifyRequest({ email: login?.data?.user?.email, secret });
    };

    const handleChange = (value: string) => {
        const intValue = value.replace(/\D/g, '').slice(0, 6);
        setSecret(intValue);
    };

    useEffect(() => {
        request({ email: login?.data?.user?.email ?? '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (qrCodeFromData?.data?.qr_code) {
            setQrCode(qrCodeFromData.data.qr_code);
        } else {
            setQrCode(null);
        }
    }, [qrCodeFromData]);

    useEffect(() => {
        if (hasSubmitted && login && !error) {
            showDefaultNotification({ type: 'success', message: translations.secretSuccess });
            navigate(`/${SITE_URLS.myProfile}`);
            // request cart information
            requestCart({});
            setHasSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, i18n.language]);

    return (
        <AuthenticationCard title={translations.title} subtitle="">
            <Form formRef={formRef} classes="d-flex flex-column" onEnterPress={submitForm} onSubmit={submitForm}>
                <>
                    <div className="mx-auto">{qrCode ? <QRCode value={qrCode} size={256} /> : ''}</div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Input
                        type={InputTypes.text}
                        label=""
                        classes="text-center"
                        variant={InputVariants.regular}
                        value={secret}
                        placeholder="000000"
                        maxLength={6}
                        onChange={(value) => handleChange(value)}
                        state={errors.secret.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.secret}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Button
                        label={translations.checkQR}
                        mode={ButtonMode.primary}
                        onClick={() => submitForm()}
                        disabled={loading}
                    />
                    {loading && ''}
                    {error && (
                        <div className="mt-1">
                            <ErrorMessages messages={[error]} />
                        </div>
                    )}
                </>
            </Form>
        </AuthenticationCard>
    );
};
